import { apiClient } from '@shared/api/apiClient';
import type { ApiResponse, PagedResult } from '@shared/types/api.types';
import type {
  PoSearchResultDto,
  PurchaseOrderDto,
  PurchaseOrderSyncResultDto,
} from '../types/purchaseOrder.types';

class PurchaseOrderService {
  private baseUrl = '/purchase-orders';

  async getAll(): Promise<PurchaseOrderDto[]> {
    const { data } = await apiClient.get<ApiResponse<PurchaseOrderDto[]>>(
      this.baseUrl
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch purchase orders');
  }

  async getAllPaged(params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDesc?: boolean;
  }): Promise<PagedResult<PurchaseOrderDto>> {
    const { data } = await apiClient.get<ApiResponse<PagedResult<PurchaseOrderDto>>>(
      `${this.baseUrl}/paged`,
      { params }
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch purchase orders');
  }

  async getById(id: number): Promise<PurchaseOrderDto> {
    const { data } = await apiClient.get<ApiResponse<PurchaseOrderDto>>(
      `${this.baseUrl}/${id}`
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch purchase order');
  }

  async syncFromNetSuite(
    syncFromDate?: string
  ): Promise<PurchaseOrderSyncResultDto> {
    const params = syncFromDate ? `?syncFromDate=${syncFromDate}` : '';
    const { data } = await apiClient.post<
      ApiResponse<PurchaseOrderSyncResultDto>
    >(`${this.baseUrl}/sync${params}`);
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to sync purchase orders');
  }

  async search(query: string): Promise<PoSearchResultDto[]> {
    const { data } = await apiClient.get<ApiResponse<PoSearchResultDto[]>>(
      `${this.baseUrl}/search`,
      { params: { query } }
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to search purchase orders');
  }

  async resync(id: number): Promise<PurchaseOrderSyncResultDto> {
    const { data } = await apiClient.post<
      ApiResponse<PurchaseOrderSyncResultDto>
    >(`${this.baseUrl}/${id}/resync`);
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to resync purchase order');
  }
}

export const purchaseOrderService = new PurchaseOrderService();
